/**
 * LovablePortfolioPromptPage — /portfolio-kit/lovable-prompt
 *
 * The Lovable prompt kit (Track A). 8 sequential prompt blocks + Supabase
 * schema + deployment notes. All Access gated at the full-prompt level;
 * free users see the structure.
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LOVABLE_PORTFOLIO_PROMPT } from "../data/portfolioKit";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";
import {
  generatePortfolioPrompt,
  listPortfolioPromptGenerations,
  type PromptGeneration,
  type PromptIntake,
} from "../api/client";
import PromptStudioWizard from "../components/portfolio-kit/PromptStudioWizard";
import GeneratedPromptView from "../components/portfolio-kit/GeneratedPromptView";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

export default function LovablePortfolioPromptPage() {
  const { t, i18n } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const lovablePrompt = L(LOVABLE_PORTFOLIO_PROMPT, i18n.language);
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const tierAccess = getTierAccess(user);
  const isAllAccess = tierAccess.canAccessProducts || tierAccess.isModerator;
  const queryClient = useQueryClient();
  const { data: generations, isLoading: loadingGenerations } = useQuery<PromptGeneration[], Error>({
    queryKey: ["portfolioPromptGenerations", "list"],
    queryFn: listPortfolioPromptGenerations,
    enabled: Boolean(isAllAccess),
  });

  const [showWizard, setShowWizard] = useState(false);
  const [lastIntake, setLastIntake] = useState<PromptIntake | null>(null);

  const generateMutation = useMutation({
    mutationFn: (intake: PromptIntake) => generatePortfolioPrompt(intake),
    onSuccess: () => {
      setShowWizard(false);
      queryClient.invalidateQueries({ queryKey: ["portfolioPromptGenerations"] });
    },
  });

  // Deep-link support: ?generation=<id> opens that specific generation
  // (e.g. from a tracker artifact's "View prompt" link). Falls back to the
  // latest generation when the param is absent or doesn't match a fetched id.
  const requestedId = searchParams.get("generation");
  const requested = requestedId
    ? generations?.find((g) => g.id === requestedId)
    : undefined;
  const latest = requested ?? generations?.[0];

  const studio = isAllAccess ? (
    <Box mb={6}>
      {loadingGenerations ? (
        <Box textAlign="center" py={4}><CircularProgress /></Box>
      ) : generateMutation.isPending ? (
        <Box textAlign="center" py={6}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {t("LovablePortfolioPromptPage.generatingMessage")}
          </Typography>
        </Box>
      ) : !latest || showWizard ? (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Trans
              i18nKey="LovablePortfolioPromptPage.wizardIntroNotice"
              t={t}
              components={[<strong key="strong0" />, <strong key="strong1" />]}
            />
          </Alert>
          {generateMutation.isError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                lastIntake ? (
                  <Button color="inherit" size="small" onClick={() => generateMutation.mutate(lastIntake)}>
                    {t("LovablePortfolioPromptPage.retryButton")}
                  </Button>
                ) : undefined
              }
            >
              {t("LovablePortfolioPromptPage.generationFailedMessage")}
            </Alert>
          )}
          <PromptStudioWizard
            generating={generateMutation.isPending}
            onSubmit={(intake) => { setLastIntake(intake); generateMutation.mutate(intake); }}
          />
        </>
      ) : (
        <GeneratedPromptView
          generation={latest}
          onRegenerate={() => setShowWizard(true)}
          onRetry={() => generateMutation.mutate(latest.intake)}
        />
      )}
    </Box>
  ) : null;

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/portfolio-kit")}
            sx={{ color: theme.palette.primary.main, mb: 2 }}
          >
            {t("LovablePortfolioPromptPage.backToPortfolioKitButton")}
          </Button>
          <Chip
            label={t("LovablePortfolioPromptPage.trackAEyebrow")}
            icon={<LockIcon sx={{ fontSize: 14 }} />}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.72rem",
              letterSpacing: "0.08em",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1.5 }}>
            {t("LovablePortfolioPromptPage.pageTitle")}
          </Typography>
          {!isAllAccess && (
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 820, lineHeight: 1.5 }}>
              {t("LovablePortfolioPromptPage.pageSubheading")}
            </Typography>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {studio}

        {!isAllAccess && (
          tierAccess.isPro ? <ProUpsellNote /> : (
            <Paper
              sx={{
                p: 4,
                mb: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              <LockIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="h5" fontWeight={500} mb={1}>
                {t("LovablePortfolioPromptPage.gateTitle")}
              </Typography>
              <Typography variant="body1" color="text.secondary" mb={2} sx={{ maxWidth: 600, mx: "auto" }}>
                {t("LovablePortfolioPromptPage.gateDescription")}
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/pricing")}
                sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
              >
                {t("LovablePortfolioPromptPage.unlockAllAccessButton")}
              </Button>
            </Paper>
          )
        )}

        {/* 8 prompt blocks — free users only; all_access sees the bespoke studio above */}
        {!isAllAccess && <Stack spacing={3}>
          {lovablePrompt.sections.map((section, idx) => (
            <Paper
              key={section.title}
              sx={{
                p: 0,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  p: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                  <Chip
                    label={t("LovablePortfolioPromptPage.blockChipLabel", { count: idx + 1 })}
                    size="small"
                    sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.common.white, fontWeight: 500, fontSize: "0.7rem" }}
                  />
                </Stack>
                <Typography variant="h5" fontWeight={500} sx={{ letterSpacing: "-0.01em" }}>
                  {section.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  {section.description}
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                    textAlign: "center",
                  }}
                >
                  <LockIcon sx={{ color: theme.palette.primary.main, fontSize: 24, mb: 1 }} />
                  <Typography variant="body2" fontWeight={500}>
                    {t("LovablePortfolioPromptPage.fullPromptLockedMessage")}
                  </Typography>
                </Paper>
              </Box>
            </Paper>
          ))}
        </Stack>}

        {/* Footer CTA */}
        <Paper sx={{ mt: 5, p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, textAlign: "center" }}>
          <Typography variant="h6" fontWeight={500} mb={1}>
            {t("LovablePortfolioPromptPage.footerCtaTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("LovablePortfolioPromptPage.footerCtaBody")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate("/portfolio-kit/demo")}
              sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
            >
              {t("LovablePortfolioPromptPage.seeJordansPortfolioButton")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/portfolio-kit")}
              sx={{ borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}
            >
              {t("LovablePortfolioPromptPage.backToPortfolioKitButton")}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
